<template>
  <div class="profile-container">
    <el-card class="profile-card">
      <div slot="header">
        <span>个人信息</span>
      </div>
      <div v-if="profileLoading" class="loading-container">
        <el-skeleton :rows="5" animated />
      </div>
      <el-form v-else :model="profileForm" label-width="120px" ref="profileForm" :rules="rules">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商家名称" prop="companyName">
              <el-input v-model="profileForm.companyName" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人" prop="contactPerson">
              <el-input v-model="profileForm.contactPerson" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系电话" prop="phone">
              <el-input v-model="profileForm.phone" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="地址" prop="address">
              <el-input type="textarea" v-model="profileForm.address" :rows="2" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item>
          <el-button type="primary" v-if="!isEditing" @click="startEdit">编辑信息</el-button>
          <template v-else>
            <el-button type="success" @click="submitForm" :loading="loading">保存</el-button>
            <el-button @click="cancelEdit">取消</el-button>
          </template>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'MerchantProfile',
  data() {
    return {
      isEditing: false,
      loading: false,
      profileLoading: false,
      profileForm: {
        id: '',
        companyName: '',
        contactPerson: '',
        phone: '',
        address: ''
      },
      originalProfile: {},
      rules: {
        companyName: [
          { required: true, message: '请输入商家名称', trigger: 'blur' }
        ],
        contactPerson: [
          { required: true, message: '请输入联系人', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '请输入地址', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadProfileData()
  },
  methods: {
    loadProfileData() {
      try {
        // 调用API获取商家信息
        this.profileLoading = true
        adminApi.getMerchantInfo()
          .then(res => {
            if (res) {
              this.profileForm = {
                id: res.id || '',
                companyName: res.companyName || '',
                contactPerson: res.contactPerson || '',
                phone: res.phone || '',
                address: res.address || ''
              }
              
              // 更新本地存储的用户信息，以便在其他地方使用
              const userInfoStr = localStorage.getItem('userInfo')
              if (userInfoStr) {
                const userInfo = JSON.parse(userInfoStr)
                const updatedUserInfo = {
                  ...userInfo,
                  id: res.id,
                  companyName: res.companyName,
                  contactPerson: res.contactPerson,
                  phone: res.phone,
                  address: res.address,
                  userName: res.companyName // 更新显示的用户名
                }
                localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo))
              }
              
              // 保存原始数据用于取消编辑时恢复
              this.originalProfile = {...this.profileForm}
            }
          })
          .catch(error => {
            this.$message.error('获取商家信息失败: ' + (error.message || '未知错误'))
            
            // 获取失败时尝试从本地存储获取信息作为备选
            this.loadFromLocalStorage()
          })
          .finally(() => {
            this.profileLoading = false
          })
      } catch (error) {
        console.error('加载个人信息失败:', error)
        this.$message.error('加载个人信息失败: ' + error.message)
        
        // 出错时从本地存储获取信息
        this.loadFromLocalStorage()
      }
    },
    loadFromLocalStorage() {
      // 从本地存储获取用户信息作为备选
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr)
        
        this.profileForm = {
          id: userInfo.id || '',
          companyName: userInfo.companyName || userInfo.userName || '',
          contactPerson: userInfo.contactPerson || '',
          phone: userInfo.phone || '',
          address: userInfo.address || ''
        }
        
        // 保存原始数据用于取消编辑时恢复
        this.originalProfile = {...this.profileForm}
      } else {
        this.$message.warning('未找到用户信息，请重新登录')
      }
    },
    startEdit() {
      this.isEditing = true
    },
    cancelEdit() {
      this.isEditing = false
      // 恢复到原始数据
      this.profileForm = {...this.originalProfile}
    },
    submitForm() {
      this.$refs.profileForm.validate(async valid => {
        if (valid) {
          this.loading = true
          try {
            // 调用API修改个人信息
            await adminApi.updateMerchant(this.profileForm)
            
            // 更新本地存储的用户信息
            const userInfoStr = localStorage.getItem('userInfo')
            if (userInfoStr) {
              const userInfo = JSON.parse(userInfoStr)
              const updatedUserInfo = {
                ...userInfo,
                companyName: this.profileForm.companyName,
                contactPerson: this.profileForm.contactPerson,
                phone: this.profileForm.phone,
                address: this.profileForm.address,
                userName: this.profileForm.companyName // 更新显示的用户名
              }
              localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo))
            }
            
            this.$message.success('个人信息更新成功')
            this.isEditing = false
            this.originalProfile = {...this.profileForm}
          } catch (error) {
            this.$message.error('更新个人信息失败: ' + (error.message || '未知错误'))
          } finally {
            this.loading = false
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style scoped>
.profile-container {
  padding: 20px;
}

.profile-card {
  max-width: 900px;
  margin: 0 auto;
}

.el-form-item {
  margin-bottom: 22px;
}

.loading-container {
  padding: 20px 0;
}
</style> 